import { i18n } from "@/i18n";

const TOKEN_KEY = "botwsp.access_token";

interface ApiErrorBody {
  code?: string;
  message?: string;
  detail?: string;
}

export class ApiError extends Error {
  status: number;
  code?: string;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message ?? body.detail ?? "Request failed");
    this.name = "ApiError";
    this.status = status;
    this.code = body.code;
  }
}

type ApiMethod = "get" | "post" | "patch" | "put" | "delete";

export interface RequestOptions {
  params?: Record<string, string | number | boolean | undefined | null>;
}

async function request<T>(
  method: ApiMethod,
  url: string,
  body?: unknown,
  options?: RequestOptions,
): Promise<{ data: T }> {
  const headers: Record<string, string> = { Accept: "application/json" };
  let payload: string | undefined;
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }

  const fullUrl = buildUrl(url, options?.params);

  const token = getAccessToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(fullUrl, { method, headers, body: payload });

  let data: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }

  if (!res.ok) {
    const body = (data && typeof data === "object" ? (data as ApiErrorBody) : {}) as ApiErrorBody;
    throw new ApiError(res.status, body);
  }

  return { data: data as T };
}

function buildUrl(url: string, params?: RequestOptions["params"]): string {
  if (!params) return url;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      search.set(key, String(value));
    }
  }
  const qs = search.toString();
  if (!qs) return url;
  return url.includes("?") ? `${url}&${qs}` : `${url}?${qs}`;
}

export const api = {
  get: <T>(url: string, options?: RequestOptions) => request<T>("get", url, undefined, options),
  post: <T>(url: string, body?: unknown) => request<T>("post", url, body),
  patch: <T>(url: string, body?: unknown) => request<T>("patch", url, body),
  put: <T>(url: string, body?: unknown) => request<T>("put", url, body),
  delete: <T = void>(url: string) => request<T>("delete", url),
};

export function getAccessToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setAccessToken(token: string | null): void {
  try {
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
  } catch {
    // Persistence is best-effort (e.g. non-browser environments).
  }
}

export function getApiErrorMessage(err: unknown, fallbackKey = "errors:request_failed"): string {
  if (err instanceof ApiError && err.code) {
    const key = `errors:${err.code}`;
    if (i18n.exists(key)) {
      return i18n.t(key);
    }
    return err.message || i18n.t(fallbackKey);
  }
  if (err instanceof Error && err.message) {
    return err.message;
  }
  return i18n.t(fallbackKey);
}
